import React, { useState, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBuilding2, LuCheck, LuLoaderCircle, LuNotebookText, LuPencil, LuPlus, LuRotateCcw, LuUsersRound } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { Field, SelectInput, TextArea, TextInput } from '../../components/form/FormControls';
import { HelpNote } from '../ProfilePage/components/ProfileUi';
import useCenterText from './centerText';
import { CENTER_UPDATED_TOAST } from './useCenters';
import { FormSection } from './parts/centerBits';

// 81 il, eski listedeki sırayla
const PROVINCES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin',
  'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
  'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan',
  'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay',
  'Isparta', 'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli',
  'Kırşehir', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş',
  'Mardin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun',
  'Siirt', 'Sinop', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa',
  'Uşak', 'Van', 'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale',
  'Batman', 'Şırnak', 'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis',
  'Osmaniye', 'Düzce',
];

/**
 * "Yeni Merkez Ekle" / "Merkez Düzenle" (Tailwind). The form logic is the old
 * one: people are picked by name and saved by id, Durum only when editing.
 * `employeesMap` (optional) names an SMO who is no longer in the active list.
 */
const AddCenterModal = ({ isOpen, onClose, onSubmit, centerData, isEditing = false, employeesMap }) => {
  const { t, language, label, statusLabel } = useCenterText();
  const uid = useId();
  // Toasts read the current language through a ref (the loaders run on open only)
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [formData, setFormData] = useState({
    Adı: '',
    İl: '',
    İlçe: '',
    Yetkili: '',
    Adres: '',
    Bilgi: '',
    SMO: '',
    SMOBackup: '',
    Durum: 'Aktif'
  });
  
  const [employees, setEmployees] = useState([]);
  const [externalPersonnels, setExternalPersonnels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If editing, populate form with existing data
    if (isEditing && centerData) {
      const yetkiliMatch = externalPersonnels.find(p => p.id === centerData.Yetkili);
      const smoMatch = employees.find(e => e.id === centerData.SMO);
      const smoBackupMatch = employees.find(e => e.id === centerData.SMOBackup);

      setFormData({
        Adı: centerData.Adı || '',
        İl: centerData.İl || '',
        İlçe: centerData.İlçe || '',
        Yetkili: yetkiliMatch ? yetkiliMatch.fullName : (centerData.Yetkili || ''),
        Adres: centerData.Adres || '',
        Bilgi: centerData.Bilgi || '',
        SMO: smoMatch ? smoMatch.fullName : (centerData.SMO || ''),
        SMOBackup: smoBackupMatch ? smoBackupMatch.fullName : (centerData.SMOBackup || ''),
        Durum: centerData.Durum || 'Aktif'
      });
    }
  }, [isEditing, centerData, employees, externalPersonnels]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeesCollection = collection(db, 'Employees');
        const employeeSnapshot = await getDocs(employeesCollection);
        const employeeList = employeeSnapshot.docs
          .filter(doc => doc.data().Active === 1) // Sadece aktif çalışanlar (Active = 1)
          .map(doc => ({
            id: doc.id,
            fullName: `${doc.data().eName} ${doc.data().eSurname}`,
            data: doc.data()
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
        setEmployees(employeeList);
        console.log('✅ Aktif çalışanlar yüklendi:', employeeList.length);
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error(msg('employeesError'));
      }
    };

    const fetchExternalPersonnels = async () => {
      try {
        const externalPersonnelsCollection = collection(db, 'ExternalPersonnels');
        const externalPersonnelSnapshot = await getDocs(externalPersonnelsCollection);
        
        // Debug: Tüm personelleri logla
        console.log('📋 Tüm harici personeller:', externalPersonnelSnapshot.docs.map(doc => ({
          id: doc.id,
          name: `${doc.data().Adı} ${doc.data().Soyadı}`,
          durum: doc.data().Durum
        })));
        
        const externalPersonnelList = externalPersonnelSnapshot.docs
          .filter(doc => {
            const durum = doc.data().Durum;
            const isActive = durum && (
              durum.toLowerCase() === 'aktif' || 
              durum === 'Aktif' ||
              durum === 'aktif'
            );
            
            // Debug: Filtrelenen personelleri logla
            if (!isActive) {
              console.log('❌ Filtrelendi:', `${doc.data().Adı} ${doc.data().Soyadı}`, 'Durum:', durum);
            }
            
            return isActive;
          })
          .map(doc => ({
            id: doc.id,
            fullName: `${doc.data().Adı} ${doc.data().Soyadı}`,
            data: doc.data()
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
        
        setExternalPersonnels(externalPersonnelList);
        console.log('✅ Aktif harici personeller yüklendi:', externalPersonnelList.length);
        console.log('📝 Aktif personel listesi:', externalPersonnelList.map(p => p.fullName));
      } catch (error) {
        console.error('Error fetching external personnels:', error);
        toast.error(msg('authoritiesError'));
      }
    };

    fetchEmployees();
    fetchExternalPersonnels();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Find selected employee documents
      const selectedYetkili = externalPersonnels.find(emp => emp.fullName === formData.Yetkili);
      const selectedSMO = employees.find(emp => emp.fullName === formData.SMO);
      const selectedSMOBackup = employees.find(emp => emp.fullName === formData.SMOBackup);

      if (isEditing) {
        // Update existing center
        const centerRef = doc(db, 'Centers', centerData.id);
        const updatedCenter = {
          ...formData,
          Yetkili: selectedYetkili?.id || formData.Yetkili || '',
          SMO: selectedSMO?.id || formData.SMO || '',
          SMOBackup: selectedSMOBackup?.id || formData.SMOBackup || '',
          updatedAt: new Date(),
        };

        await updateDoc(centerRef, updatedCenter);
        toast.success(msg('updated'), { toastId: CENTER_UPDATED_TOAST });
      } else {
        // Add new center
        const centersRef = collection(db, 'Centers');
        const newCenter = {
          ...formData,
          Yetkili: selectedYetkili?.id || '',
          SMO: selectedSMO?.id || '',
          SMOBackup: selectedSMOBackup?.id || '',
          createdAt: new Date(),
        };

        await addDoc(centersRef, newCenter);
        toast.success(msg('formAdded'));
      }
      
      if (onSubmit) {
        onSubmit(formData);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error with center operation:', error);
      toast.error(isEditing 
        ? msg('formUpdateError') 
        : msg('formAddError')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditing && centerData) {
      const yetkiliMatch = externalPersonnels.find(p => p.id === centerData.Yetkili);
      const smoMatch = employees.find(e => e.id === centerData.SMO);

      const smoBackupMatch = employees.find(e => e.id === centerData.SMOBackup);
      setFormData({
        Adı: centerData.Adı || '',
        İl: centerData.İl || '',
        İlçe: centerData.İlçe || '',
        Yetkili: yetkiliMatch ? yetkiliMatch.fullName : (centerData.Yetkili || ''),
        Adres: centerData.Adres || '',
        Bilgi: centerData.Bilgi || '',
        SMO: smoMatch ? smoMatch.fullName : (centerData.SMO || ''),
        SMOBackup: smoBackupMatch ? smoBackupMatch.fullName : (centerData.SMOBackup || ''),
        Durum: centerData.Durum || 'Aktif'
      });
    } else {
      setFormData({
        Adı: '',
        İl: '',
        İlçe: '',
        Yetkili: '',
        Adres: '',
        Bilgi: '',
        SMO: '',
        SMOBackup: '',
        Durum: 'Aktif'
      });
    }
  };

  // A stored value that is not in the (active) list stays visible instead of "Seçiniz"
  const withStored = (options, value, name) => (
    value && !options.some((option) => option.value === value)
      ? [{ value, label: label('form.notInList', { name: name || value }) }, ...options]
      : options
  );
  const employeeName = (value) => (employeesMap && employeesMap[value]) || value;
  const provinceOptions = withStored(PROVINCES.map((name) => ({ value: name, label: name })), formData.İl);
  const authorityOptions = withStored(externalPersonnels.map((emp) => ({ value: emp.fullName, label: emp.fullName })), formData.Yetkili);
  const smoOptions = withStored(employees.map((emp) => ({ value: emp.fullName, label: emp.fullName })), formData.SMO, employeeName(formData.SMO));
  const smoBackupOptions = withStored(
    employees
      .filter(emp => emp.fullName !== formData.SMO)
      .map((emp) => ({ value: emp.fullName, label: emp.fullName })),
    formData.SMOBackup,
    employeeName(formData.SMOBackup),
  );
  const id = (name) => `${uid}-${name}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEditing ? label('form.titleEdit') : label('form.titleAdd')}
      subtitle={isEditing ? (centerData?.Adı || undefined) : label('form.subtitleAdd')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient={isEditing ? 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' : 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30'} size="xs">
          {isEditing ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading} className="sm:tw-mr-auto">{label('common.cancel')}</Button>
          <Button onClick={handleReset} disabled={loading}>
            <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('form.reset')}
          </Button>
          <Button variant="primary" type="submit" form={id('form')} disabled={loading} className="max-sm:tw-flex-1">
            {loading
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={3} />}
            {loading ? label('form.processing') : isEditing ? label('form.update') : label('form.add')}
          </Button>
        </>
      )}
    >
      <form id={id('form')} onSubmit={handleSubmit} className="tw-space-y-7">
        <FormSection icon={LuBuilding2} title={label('form.sections.basic')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('form.name')} htmlFor={id('name')} required className="sm:tw-col-span-2">
              <TextInput id={id('name')} name="Adı" value={formData.Adı} onChange={handleChange} required />
            </Field>
            <Field label={label('form.city')} htmlFor={id('city')} required>
              <SelectInput id={id('city')} name="İl" value={formData.İl} onChange={handleChange} required placeholder={label('form.choose')} options={provinceOptions} />
            </Field>
            <Field label={label('form.district')} htmlFor={id('district')} required>
              <TextInput id={id('district')} name="İlçe" value={formData.İlçe} onChange={handleChange} required />
            </Field>
            {isEditing && (
              <Field label={label('form.status')} htmlFor={id('status')} required>
                <SelectInput
                  id={id('status')}
                  name="Durum"
                  value={formData.Durum}
                  onChange={handleChange}
                  required
                  options={[{ value: 'Aktif', label: statusLabel('Aktif') }, { value: 'İnaktif', label: statusLabel('İnaktif') }]}
                />
              </Field>
            )}
          </div>
        </FormSection>

        <FormSection icon={LuUsersRound} title={label('form.sections.people')}>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Field label={label('form.authority')} htmlFor={id('authority')} hint={label('form.authorityHint')} className="sm:tw-col-span-2">
              <SelectInput id={id('authority')} name="Yetkili" value={formData.Yetkili} onChange={handleChange} placeholder={label('form.choose')} options={authorityOptions} />
            </Field>
            <Field label={label('form.smo')} htmlFor={id('smo')}>
              <SelectInput id={id('smo')} name="SMO" value={formData.SMO} onChange={handleChange} placeholder={label('form.choose')} options={smoOptions} />
            </Field>
            <Field label={label('form.smoBackup')} htmlFor={id('smoBackup')}>
              <SelectInput id={id('smoBackup')} name="SMOBackup" value={formData.SMOBackup} onChange={handleChange} placeholder={label('form.choose')} options={smoBackupOptions} />
            </Field>
            <HelpNote className="sm:tw-col-span-2">{label('form.peopleHint')}</HelpNote>
          </div>
        </FormSection>

        <FormSection icon={LuNotebookText} title={label('form.sections.notes')}>
          <div className="tw-grid tw-gap-4">
            <Field label={label('form.address')} htmlFor={id('address')}>
              <TextArea id={id('address')} name="Adres" value={formData.Adres} onChange={handleChange} rows={3} />
            </Field>
            <Field label={label('form.info')} htmlFor={id('info')}>
              <TextArea id={id('info')} name="Bilgi" value={formData.Bilgi} onChange={handleChange} rows={3} />
            </Field>
          </div>
        </FormSection>
      </form>
    </Modal>
  );
};

AddCenterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  centerData: PropTypes.object,
  isEditing: PropTypes.bool,
  employeesMap: PropTypes.object,
};

export default AddCenterModal;
